// What this EmCog has (Oct 25 2026, the workspace port to UPM): the server says which places, settings, connection
// cards, add-on keys and guides it can back, so the workspace never shows a page for an organ that isn't there. A
// server without the route (an EmCog from before it) has everything — the answer is all-on.
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface Features {
  places: string[];
  settings: string[];
  connections: string[];
  keys: string[];
  guides: string[];
  computer: boolean;
  adventure: boolean;
}

/** Everything on — what a server without /api/workspace/features has. */
export const ALL_FEATURES: Features = {
  places: ['chat', 'projects', 'library', 'lists', 'seeds', 'schedule', 'town', 'activity'],
  settings: ['general', 'mind', 'voice', 'connections', 'desktop', 'appearance', 'guides'],
  connections: ['address', 'computer', 'mail', 'town', 'telegram', 'google', 'connectors'],
  keys: ['replicate', 'google_maps', 'vectorizer_ai', 'home_assistant', 'spotify', 'twitter'],
  guides: ['address', 'computer', 'mail', 'telegram', 'google', 'town', 'connectors', 'voice', 'services', 'dnd', 'themes'],
  computer: true,
  adventure: true
};

// One answer per token for the whole page: the features don't change while it's open.
let asked: { token: string | null; answer: Promise<Features> } | null = null;

function load(token: string | null): Promise<Features> {
  if (asked && asked.token === token) return asked.answer;
  const answer = fetch('/api/workspace/features', { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then(async res => {
      if (!res.ok) return ALL_FEATURES;
      const f = await res.json();
      return f && Array.isArray(f.places) ? { ...ALL_FEATURES, ...f } as Features : ALL_FEATURES;
    })
    .catch(() => ALL_FEATURES);
  asked = { token, answer };
  return answer;
}

/** This EmCog's features — null until the server has answered. */
export function useFeatures(): Features | null {
  const { token } = useAuth();
  const [features, setFeatures] = useState<Features | null>(null);
  useEffect(() => {
    let live = true;
    load(token || null).then(f => { if (live) setFeatures(f); });
    return () => { live = false; };
  }, [token]);
  return features;
}
